import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useEffect, useMemo, useRef } from 'react';
import { Animated, Modal, Platform, Pressable, StyleSheet, Text } from 'react-native';

import { fontSize, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { playSuccess } from '@/lib/sounds';
import { SonarPulse } from '@/components/ui/SonarPulse';

type Props = {
  visible: boolean;
  message?: string;
  onDone?: () => void;
  /** 'check' — κλασικό ✓· 'antenna' — κεραία που εκπέμπει σήμα (π.χ. "δημοσιεύτηκε"). */
  variant?: 'check' | 'antenna';
};

/** Πλήρους οθόνης επιβεβαίωση με animated checkmark ή "κεραία σήματος" (scale-in). */
export function SuccessOverlay({ visible, message, onDone, variant = 'check' }: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const scale = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) return;
    scale.setValue(0);
    opacity.setValue(0);
    if (Platform.OS !== 'web') {
      void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
    playSuccess();
    Animated.parallel([
      Animated.spring(scale, { toValue: 1, useNativeDriver: Platform.OS !== 'web', friction: 5, tension: 80 }),
      Animated.timing(opacity, { toValue: 1, duration: 180, useNativeDriver: Platform.OS !== 'web' }),
    ]).start();
    const t = setTimeout(() => onDone?.(), 1700);
    return () => clearTimeout(t);
  }, [visible, onDone, scale, opacity]);

  if (!visible) return null;

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onDone}>
      <Pressable style={styles.overlay} onPress={onDone}>
        <Animated.View style={[styles.circle, { opacity, transform: [{ scale }] }]}>
          {variant === 'antenna' ? (
            <>
              <SonarPulse active size={150} />
              <Ionicons name="radio-outline" size={48} color="#fff" />
            </>
          ) : (
            <Text style={styles.check}>✓</Text>
          )}
        </Animated.View>
        {!!message && <Text style={styles.message}>{message}</Text>}
      </Pressable>
    </Modal>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.lg,
      backgroundColor: 'rgba(15,23,42,0.85)',
      padding: spacing.lg,
    },
    circle: {
      width: 110,
      height: 110,
      borderRadius: 55,
      backgroundColor: colors.success,
      alignItems: 'center',
      justifyContent: 'center',
    },
    check: {
      color: '#FFFFFF',
      fontSize: 64,
      fontWeight: '800',
      lineHeight: 72,
    },
    message: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
    },
  });
}
